/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';

import { selectDraftEnvironmentId } from '@ibm-watson/shared/modules/ia/store/environments/selectors';
import { ApiStatus } from '@ibm-watson/shared/ts';
import { RootState } from '@ibm-watson/shared/ts/state';
import { FIELDS as CatalogFields } from '../../components/catalog-openapi/schema';
import {
  CATALOG_CHANNEL_TYPE,
  SEGMENT_EXTENSION_TYPE
} from '../../util/channelTypes';
import { selectAllIntegrations } from '../channels/selectors';
import { catalogAdapter } from './adapter';

const catalogSelectors = catalogAdapter?.getSelectors(
  (state: RootState) => state.catalog
);

export const selectAllCatalogItems = catalogSelectors?.selectAll;
export const selectCatalogItemById = catalogSelectors.selectById;

export const selectSegmentCatalogItemId = (state: RootState) =>
  selectAllCatalogItems(state)?.find(
    item => item.specification?.type === SEGMENT_EXTENSION_TYPE
  )?.catalog_item_id;

export const selectDraftCatalogItem = (state: RootState) =>
  state.catalog?.draftCatalogItem?.catalogItem;

export const selectDraftCatalogErrors = (state: RootState) => {
  return {
    importErrors: state.catalog?.draftCatalogItem?.importErrors,
    importErrorSubject: state.catalog?.draftCatalogItem?.importErrorSubject
  };
};

export const areCatalogItemsLoading = (state: RootState): boolean =>
  state.catalog?.listCatalogItems?.status === ApiStatus.LOADING;

export const getCatalogItemIdFromDraftIntegration = (
  state: RootState
): string | null =>
  _.get(
    state.integrations?.draftIntegration?.integration || {},
    CatalogFields.CATALOG_ITEM_ID
  );

export const selectCatalogItemFromIntegrationId = (state: RootState) => {
  const catalogItemId = getCatalogItemIdFromDraftIntegration(state);

  if (_.isEmpty(catalogItemId)) {
    return null;
  }

  return selectCatalogItemById(state, catalogItemId);
};

export const isSegmentCatalogItemId = (
  state: RootState,
  catalogItemId: string
): boolean => {
  const catalogItem = selectCatalogItemById(state, catalogItemId);
  return (
    _.get(catalogItem, 'specification.type') === 'segment' ||
    _.get(catalogItem, 'name') === 'segment'
  );
};

export const selectAllDraftCatalogIntegrations = createSelector(
  [selectAllIntegrations, selectDraftEnvironmentId],
  (integrations, draftEnvironmentId) =>
    integrations.filter(
      integration =>
        integration.type === CATALOG_CHANNEL_TYPE &&
        integration.environment_id === draftEnvironmentId
    )
);
